/**
 * A finished project — `client/projects.html` §Delivered.
 *
 * A card rather than a region (CONTRACT.md §4.4: borders separate, whitespace
 * groups) because a delivered project is a *different kind of thing* from the one
 * under way: it has no schedule left to read and no figure to watch. It gets one
 * `Answer` — what it was, when it landed and what it cost — the scope in the
 * client's own words, and a link to the files.
 *
 * No chip and no pill: §4.3, "don't add a chip whose word is already in the
 * sentence". The sentence says "Delivered 22 May 2026".
 */

import { Link } from '@tanstack/react-router'
import type { InvoiceView } from '@fareast/shared'

import { Card, CardBody } from '../../../components/portal/Card.js'
import { Push, Row, Stack } from '../../../components/portal/Layout.js'
import { Tag } from '../../../components/portal/Status.js'
import {
  Answer,
  B,
  DateText,
  More,
  Muted,
  Num,
  Prose,
  Ref,
  Small,
} from '../../../components/portal/Text.js'
import { invoicesForProject, type ProjectRow } from './facts.js'
import { projectAnchor } from './LiveProject.js'

export type DeliveredProjectProps = {
  readonly project: ProjectRow
  readonly invoices: readonly InvoiceView[]
  /** The rate card's name for `work_order.template` — the mockup's `.tag`, or null. */
  readonly templateName: string | null
}

export function DeliveredProject({ project, invoices, templateName }: DeliveredProjectProps) {
  const { work_order: order, milestones } = project
  const mine = invoicesForProject(invoices, project)

  return (
    <Card id={projectAnchor(order.ref)}>
      <CardBody>
        <Stack>
          <Row>
            <B>{order.title}</B>
            <Ref value={order.ref} />
            {templateName === null ? null : <Tag>{templateName}</Tag>}
            <Push />
            <More asChild>
              <Link to="/app/files">Files &rarr;</Link>
            </More>
          </Row>

          <Answer>
            {order.delivered_at === null ? (
              <>Delivered.</>
            ) : (
              <>
                Delivered <DateText value={order.delivered_at} />
              </>
            )}
            {milestones.length === 1 && milestones[0]?.percent === 100 ? (
              <> &mdash; one milestone, 100% on delivery.</>
            ) : (
              <> &mdash; {String(milestones.length)} milestones.</>
            )}
          </Answer>

          {mine.map((invoice) => (
            <Small key={invoice.ref}>
              <Muted>
                Invoiced <Ref value={invoice.ref} /> at <Num value={invoice.total} />
                {invoice.paid_at === null ? (
                  <>, not yet paid.</>
                ) : (
                  <>
                    {' '}
                    and paid <DateText value={invoice.paid_at} />.
                  </>
                )}
              </Muted>
            </Small>
          ))}

          <Prose>{order.scope}</Prose>
        </Stack>
      </CardBody>
    </Card>
  )
}
